import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { denormalisedResponseEntities } from '../../../../util/data';
import { useIntl } from '../../../../util/reactIntl';

import SectionContainer from '../SectionContainer';

import StarIcon from '../../../../assets/star.svg';

import css from './Testimonials.module.css';

// Swiper CSS is browser-only; top-level import breaks Node SSR.

const LISTINGS_SAMPLE_SIZE = 6;
const REVIEWS_FETCH_LIMIT = 6;
const REVIEWS_DISPLAY_COUNT = 3;
const SWIPER_DESKTOP_BREAKPOINT = 1024;
const AUTOPLAY_DELAY_MS = 5000;

/**
 * Fetch public listing reviews by sampling published listings.
 * Marketplace API requires listing_id (or subject/transaction id) per reviews.query.
 *
 * @returns {Function} Redux thunk that resolves to up to REVIEWS_FETCH_LIMIT reviews
 */
const fetchListingReviews = () => (dispatch, getState, sdk) => {
  return sdk.listings
    .query({ perPage: LISTINGS_SAMPLE_SIZE })
    .then(listingsResponse => {
      const listings = listingsResponse?.data?.data || [];
      const listingIds = listings.map(listing => listing.id).filter(Boolean);

      return Promise.allSettled(
        listingIds.map(listingId =>
          sdk.reviews
            .query({
              listing_id: listingId,
              state: 'public',
              include: ['author', 'listing'],
            })
            .then(response => denormalisedResponseEntities(response))
        )
      );
    })
    .then(results => {
      const reviews = results
        .filter(result => result.status === 'fulfilled')
        .flatMap(result => result.value || [])
        .filter(review => {
          const { content, rating, deleted } = review?.attributes || {};
          return !!content && typeof rating === 'number' && !deleted;
        })
        .sort((a, b) => {
          const aTime = a.attributes?.createdAt ? new Date(a.attributes.createdAt).getTime() : 0;
          const bTime = b.attributes?.createdAt ? new Date(b.attributes.createdAt).getTime() : 0;
          return bTime - aTime;
        })
        .slice(0, REVIEWS_FETCH_LIMIT);

      return reviews;
    });
};

const TestimonialsComponent = props => {
  const { sectionId, className, rootClassName, appearance, options, onFetchListingReviews } = props;
  const intl = useIntl();
  const ratingStarAlt = intl.formatMessage({ id: 'PageBuilder.Testimonials.ratingStarAlt' });
  const [reviews, setReviews] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    import('swiper/css');
    import('swiper/css/pagination');
  }, []);

  useEffect(() => {
    let isMounted = true;

    onFetchListingReviews()
      .then(fetchedReviews => {
        if (isMounted) {
          setReviews(Array.isArray(fetchedReviews) ? fetchedReviews : []);
          setHasLoaded(true);
        }
      })
      .catch(() => {
        if (isMounted) {
          setReviews([]);
          setHasLoaded(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [onFetchListingReviews]);

  const displayedReviews = reviews.slice(0, REVIEWS_DISPLAY_COUNT);

  // Hide until we have a full 3-up set so the desktop carousel layout stays intact.
  if (!hasLoaded || displayedReviews.length < REVIEWS_DISPLAY_COUNT) {
    return null;
  }

  return (
    <SectionContainer
      id={sectionId}
      rootClassName={classNames(rootClassName || css.root)}
      className={className}
      appearance={appearance}
      options={options}
    >
      <div className={css.testimonials}>
        <Swiper
          className={css.swiper}
          modules={[Autoplay, Pagination]}
          slidesPerView={1}
          spaceBetween={10}
          pagination={{ clickable: true }}
          autoplay={{
            delay: AUTOPLAY_DELAY_MS,
            disableOnInteraction: false,
          }}
          breakpoints={{
            [SWIPER_DESKTOP_BREAKPOINT]: {
              slidesPerView: 3,
              spaceBetween: 20,
              autoplay: false,
              pagination: { enabled: true },
            },
          }}
          watchOverflow
        >
          {displayedReviews.map(review => {
            const rating = review.attributes?.rating || 0;
            const title = review.listing?.attributes?.title || '';
            const text = review.attributes?.content || '';
            const name = review.author?.attributes?.profile?.displayName || '';
            const reviewKey = review.id?.uuid || review.id;

            return (
              <SwiperSlide key={reviewKey} className={classNames(css.slide, css.slideDesktop)}>
                <div className={css.testimonial}>
                  <div className={css.testimonialRating}>
                    {Array.from({ length: rating }).map((_, index) => (
                      <img src={StarIcon} alt={ratingStarAlt} key={index} />
                    ))}
                  </div>
                  <p className={css.testimonialTitle}>{title}</p>
                  <p className={css.testimonialText}>{text}</p>
                  <p className={css.testimonialName}>{name}</p>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </SectionContainer>
  );
};

const mapDispatchToProps = {
  onFetchListingReviews: fetchListingReviews,
};

const Testimonials = connect(null, mapDispatchToProps)(TestimonialsComponent);

export default Testimonials;
